// src/utils/analytics.js
// Analytics and performance tracking for CoCafe Host

/**
 * Core Web Vitals tracking
 */
export const webVitals = {
  /**
   * Largest Contentful Paint - time when largest element becomes visible
   */
  trackLCP() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          trackEvent('web_vital', {
            metric: 'LCP',
            value: Math.round(lastEntry.renderTime || lastEntry.loadTime),
            unit: 'ms',
          });
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP tracking not supported');
      }
    }
  },

  /**
   * First Input Delay - time from first user input to response
   */
  trackFID() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            trackEvent('web_vital', {
              metric: 'FID',
              value: Math.round(entry.processingDuration),
              unit: 'ms',
            });
          }
        });
        observer.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID tracking not supported');
      }
    }
  },

  /**
   * Cumulative Layout Shift - visual stability
   */
  trackCLS() {
    if ('PerformanceObserver' in window) {
      try {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              trackEvent('web_vital', {
                metric: 'CLS',
                value: Math.round(clsValue * 1000) / 1000,
              });
            }
          }
        });
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS tracking not supported');
      }
    }
  },
};

/**
 * Login conversion funnel tracking
 */
export const conversionFunnel = {
  trackLoginPageView() {
    trackEvent('login_funnel', {
      step: 'page_view',
      timestamp: new Date().toISOString(),
    });
  },

  trackLoginAttempt(email) {
    trackEvent('login_funnel', {
      step: 'login_attempt',
      email_domain: email.split('@')[1] || 'unknown',
      timestamp: new Date().toISOString(),
    });
  },

  trackLoginSuccess(email, loginMethod = 'email') {
    trackEvent('login_funnel', {
      step: 'login_success',
      method: loginMethod,
      email_domain: email.split('@')[1] || 'unknown',
      timestamp: new Date().toISOString(),
    });
  },

  trackLoginError(errorType) {
    trackEvent('login_funnel', {
      step: 'login_error',
      error_type: errorType,
      timestamp: new Date().toISOString(),
    });
  },

  trackSignupView() {
    trackEvent('signup_funnel', {
      step: 'page_view',
      timestamp: new Date().toISOString(),
    });
  },

  trackSignupStarted() {
    trackEvent('signup_funnel', {
      step: 'form_started',
      timestamp: new Date().toISOString(),
    });
  },

  trackSignupCompletion() {
    trackEvent('signup_funnel', {
      step: 'signup_success',
      timestamp: new Date().toISOString(),
    });
  },
};

/**
 * Form abandonment tracking
 */
export const formTracking = {
  trackFormFocus(formName) {
    trackEvent('form_interaction', {
      form: formName,
      action: 'focus',
      timestamp: new Date().toISOString(),
    });
  },

  trackFieldInput(formName, fieldName) {
    trackEvent('form_interaction', {
      form: formName,
      field: fieldName,
      action: 'input',
      timestamp: new Date().toISOString(),
    });
  },

  trackFormAbandonment(formName, completedFields) {
    trackEvent('form_abandonment', {
      form: formName,
      completed_fields: completedFields,
      total_fields: 5, // adjust based on form
      completion_rate: (completedFields / 5 * 100).toFixed(2) + '%',
      timestamp: new Date().toISOString(),
    });
  },

  trackFormSubmission(formName, success) {
    trackEvent('form_submission', {
      form: formName,
      success: success,
      timestamp: new Date().toISOString(),
    });
  },
};

/**
 * Core event tracking function
 * In production, integrate with Google Analytics, Mixpanel, or similar
 */
export function trackEvent(eventName, eventData = {}) {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', eventName, eventData);
  }

  // Send to analytics endpoint in production
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    sendAnalyticsEvent(eventName, eventData);
  }
}

/**
 * Send event to analytics endpoint
 */
async function sendAnalyticsEvent(eventName, eventData) {
  try {
    // Replace with your actual analytics endpoint
    // await fetch('/api/analytics', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ event: eventName, data: eventData })
    // });
  } catch (error) {
    // Silently fail - don't break the app if analytics fails
    console.error('Analytics error:', error);
  }
}

/**
 * Page performance summary
 */
export function getPerformanceSummary() {
  if ('performance' in window) {
    const perfData = window.performance.timing;
    return {
      pageLoadTime: perfData.loadEventEnd - perfData.navigationStart,
      domReadyTime: perfData.domContentLoadedEventEnd - perfData.navigationStart,
      resourceLoadTime: perfData.responseEnd - perfData.fetchStart,
      timeToFirstByte: perfData.responseStart - perfData.navigationStart,
    };
  }
  return null;
}

/**
 * Initialize all tracking
 */
export function initializeAnalytics() {
  // Track Core Web Vitals
  webVitals.trackLCP();
  webVitals.trackFID();
  webVitals.trackCLS();

  // Track page view
  trackEvent('page_view', {
    url: window.location.pathname,
    timestamp: new Date().toISOString(),
  });

  // Log performance summary
  const perfSummary = getPerformanceSummary();
  if (perfSummary) {
    trackEvent('performance', perfSummary);
  }
}
